import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { collection, addDoc, query, where, getDocs, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { IoClose, IoLockClosed, IoTrash, IoCalendarOutline, IoWarningOutline, IoCheckmarkCircleOutline } from 'react-icons/io5';
import { useAuth } from '../../context/AuthContext';
import moment from 'moment';

function CierreFiscalModal({ onClose }) {
    const { userData } = useAuth();
    const [existingCierres, setExistingCierres] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear() - 1);
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);

    // Modal Notifications & Confirmations
    const [deleteTarget, setDeleteTarget] = useState(null); // { id, year }
    const [successMessage, setSuccessMessage] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    useEffect(() => {
        fetchExistingCierres();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchExistingCierres = async () => {
        if (!userData?.condominioId) return;
        setLoading(true);
        try {
            const q = query(
                collection(db, 'contabilidad_asientos'),
                where('condominioId', '==', userData.condominioId),
                where('tipoReferencia', '==', 'CIERRE')
            );
            const snap = await getDocs(q);
            const list = [];
            snap.forEach(d => {
                const data = d.data();
                const year = data.referenceId?.replace('CIERRE_', '');
                list.push({
                    id: d.id,
                    year: year || 'N/A',
                    fecha: data.createdAt ? moment(data.createdAt).format('DD/MM/YYYY HH:mm') : 'N/A',
                    monto: data.totalAmount || 0
                });
            });
            list.sort((a, b) => b.year - a.year);
            setExistingCierres(list);
        } catch (e) {
            console.error("Error fetching closures:", e);
        } finally {
            setLoading(false);
        }
    };

    const handleRunCierre = async () => {
        if (!userData?.condominioId) return;

        // Check if selected year is already closed
        const alreadyClosed = existingCierres.some(c => Number(c.year) === selectedYear);
        if (alreadyClosed) {
            setErrorMessage(`El ejercicio fiscal del año ${selectedYear} ya se encuentra cerrado.`);
            return;
        }

        setProcessing(true);
        try {
            // 1. Fetch all accounts mapping
            const accountsSnap = await getDocs(query(collection(db, "cuentas_contables"), where("condominioId", "==", userData.condominioId)));
            const accounts = [];
            accountsSnap.forEach(d => {
                accounts.push({ id: d.id, ...d.data() });
            });

            // 2. Fetch all posted ledger entries (contabilidad_asientos) for this condo up to the end of the year
            const yearEndDate = moment().year(selectedYear).month(11).date(31).hour(23).minute(59).second(59);
            const qAsientos = query(
                collection(db, 'contabilidad_asientos'),
                where('condominioId', '==', userData.condominioId)
            );
            const snapAsientos = await getDocs(qAsientos);

            // 3. Accumulate balances for all accounts
            const balances = {}; // accountId -> balance
            accounts.forEach(acc => {
                balances[acc.id] = 0;
            });

            snapAsientos.forEach(d => {
                const entry = d.data();
                // Check if entry date is before or on yearEndDate
                const entryDate = entry.fecha?.toDate ? moment(entry.fecha.toDate()) : moment(entry.createdAt);
                if (entryDate.isAfter(yearEndDate)) return;

                // Sum up debits and credits
                if (entry.detalles && Array.isArray(entry.detalles)) {
                    entry.detalles.forEach(det => {
                        const accountId = det.cuentaId;
                        const debe = Number(det.debe || 0);
                        const haber = Number(det.haber || 0);
                        if (balances[accountId] !== undefined) {
                            balances[accountId] += (debe - haber);
                        }
                    });
                }
            });

            // 4. Calculate Net Profit/Loss of the selected year
            // Revenues (Ingresos: codes starting with '4') and Expenses (Egresos: codes starting with '5')
            let netResult = 0; // Revenues - Expenses
            const details = [];

            accounts.forEach(acc => {
                const accNum = String(acc.numero || acc.codigo || '');
                const balance = balances[acc.id] || 0;

                if (balance === 0) return;

                if (accNum.startsWith('4')) {
                    // Revenues (Creditor nature: Credit - Debit. If balance is negative, it means credit > debit)
                    // We cancel it by debiting it (debe: -balance)
                    // (Note: in our database balance = debe - haber, so credit > debit makes balance negative)
                    const absoluteCreditBalance = -balance;
                    if (absoluteCreditBalance !== 0) {
                        details.push({
                            cuentaId: acc.id,
                            debe: absoluteCreditBalance > 0 ? absoluteCreditBalance : 0,
                            haber: absoluteCreditBalance < 0 ? -absoluteCreditBalance : 0,
                            description: `Cancelación cuenta de ingresos ${accNum} para Cierre`
                        });
                        netResult += absoluteCreditBalance; // Credit adds to profit
                    }
                } else if (accNum.startsWith('5') || accNum.startsWith('6')) {
                    // Expenses (Debtor nature: Debit - Credit. If balance is positive, it means debit > credit)
                    // We cancel it by crediting it (haber: balance)
                    const absoluteDebitBalance = balance;
                    if (absoluteDebitBalance !== 0) {
                        details.push({
                            cuentaId: acc.id,
                            debe: absoluteDebitBalance < 0 ? -absoluteDebitBalance : 0,
                            haber: absoluteDebitBalance > 0 ? absoluteDebitBalance : 0,
                            description: `Cancelación cuenta de egresos ${accNum} para Cierre`
                        });
                        netResult -= absoluteDebitBalance; // Debit subtracts from profit
                    }
                }
            });

            // 5. Post to Capital Account (Account code 3020: Resultados de Ejercicios Anteriores)
            // Look for capital account
            let capitalAcc = accounts.find(a => String(a.numero || a.codigo) === '3020');
            if (!capitalAcc) {
                capitalAcc = accounts.find(a => String(a.numero || a.codigo).startsWith('3'));
            }

            if (!capitalAcc) {
                throw new Error("No se encontró la cuenta contable patrimonial 3020 o alguna de Capital (Clase 3) para registrar el Resultado del Ejercicio.");
            }

            if (netResult > 0) {
                // Profit: Net credit balance. We Credit Capital account to increase it.
                details.push({
                    cuentaId: capitalAcc.id,
                    debe: 0,
                    haber: netResult,
                    description: `Utilidad neta acumulada traspasada a Capital`
                });
            } else if (netResult < 0) {
                // Loss: Net debit balance. We Debit Capital account to decrease it.
                details.push({
                    cuentaId: capitalAcc.id,
                    debe: -netResult,
                    haber: 0,
                    description: `Pérdida neta acumulada traspasada a Capital`
                });
            }

            // Calculate total journal amount (sum of debits)
            const totalJournalAmount = details.reduce((sum, r) => sum + (r.debe || 0), 0);

            // 6. Post Closing Entry
            const closingDate = moment().year(selectedYear).month(11).date(31).hour(23).minute(59).second(59).toDate();
            await addDoc(collection(db, 'contabilidad_asientos'), {
                condominioId: userData.condominioId,
                fecha: Timestamp.fromDate(closingDate),
                concepto: `Cierre del Ejercicio Fiscal ${selectedYear}`,
                referenceId: `CIERRE_${selectedYear}`,
                tipoReferencia: 'CIERRE',
                totalAmount: totalJournalAmount,
                detalles: details,
                createdAt: new Date().toISOString(),
                createdBy: userData?.email || 'Sistema'
            });

            setSuccessMessage(`Cierre fiscal del año ${selectedYear} generado exitosamente.`);
            fetchExistingCierres();
        } catch (e) {
            console.error("Error executing year close:", e);
            setErrorMessage("Error al procesar el cierre fiscal: " + e.message);
        } finally {
            setProcessing(false);
        }
    };

    const executeDeleteCierre = async () => {
        if (!deleteTarget) return;
        const { id, year } = deleteTarget;
        setDeleteTarget(null);

        setProcessing(true);
        try {
            await deleteDoc(doc(db, 'contabilidad_asientos', id));
            setSuccessMessage(`Cierre fiscal del año ${year} eliminado exitosamente. El periodo está abierto.`);
            fetchExistingCierres();
        } catch (e) {
            console.error("Error deleting closing entry:", e);
            setErrorMessage("Error al eliminar el cierre fiscal: " + e.message);
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="modal-overlay" style={{ zIndex: 999 }}>
            {deleteTarget && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-content" style={{ maxWidth: '420px', textAlign: 'center', padding: '30px' }}>
                        <IoWarningOutline size={60} color="#f59e0b" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem', fontWeight: 'bold' }}>
                            ¿Reabrir Ejercicio Fiscal?
                        </h3>
                        <p style={{ color: '#4b5563', marginBottom: '25px', fontSize: '0.95rem', lineHeight: '1.5' }}>
                            ¿Estás seguro de que deseas eliminar el Cierre Fiscal del año <strong>{deleteTarget.year}</strong>? Esto reabrirá las cuentas del periodo y recalculará los saldos históricos.
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button 
                                className="btn-cancel" 
                                style={{ flex: 1, padding: '12px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', borderRadius: '10px' }} 
                                onClick={() => setDeleteTarget(null)}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="btn-confirm" 
                                style={{ flex: 1, padding: '12px', fontSize: '1rem', fontWeight: 'bold', background: '#ef4444', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '10px' }} 
                                onClick={executeDeleteCierre}
                            >
                                Reabrir
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {successMessage && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
                        <IoCheckmarkCircleOutline size={60} color="#10b981" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem', fontWeight: 'bold' }}>
                            Operación Exitosa
                        </h3>
                        <p style={{ color: '#4b5563', marginBottom: '20px', fontSize: '0.95rem' }}>
                            {successMessage}
                        </p>
                        <button 
                            className="btn-confirm" 
                            style={{ width: '100%', padding: '12px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', borderRadius: '10px' }} 
                            onClick={() => setSuccessMessage(null)}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            )}

            {errorMessage && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
                        <IoWarningOutline size={60} color="#ef4444" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem', fontWeight: 'bold' }}>
                            Error
                        </h3>
                        <p style={{ color: '#4b5563', marginBottom: '20px', fontSize: '0.95rem' }}>
                            {errorMessage}
                        </p>
                        <button 
                            className="btn-confirm" 
                            style={{ width: '100%', padding: '12px', fontSize: '1rem', fontWeight: 'bold', background: '#ef4444', cursor: 'pointer', borderRadius: '10px' }} 
                            onClick={() => setErrorMessage(null)}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            )}

            <div className="modal-content" style={{ maxWidth: '600px', display: 'flex', flexDirection: 'column' }}>
                <div className="modal-header">
                    <h3><IoLockClosed /> Cierre de Ejercicio Fiscal</h3>
                    <button onClick={onClose} className="close-btn"><IoClose /></button>
                </div>

                <div className="modal-body" style={{ padding: '20px' }}>
                    <div style={{ background: '#f3f4f6', padding: '15px', borderRadius: '8px', marginBottom: '20px', fontSize: '0.9rem', color: '#4b5563', lineHeight: '1.4' }}>
                        💡 <strong>¿Cómo funciona?</strong> Al cerrar el ejercicio, el sistema cancela los saldos de todas las cuentas de Ingresos y Gastos (llevándolas a cero para el siguiente año) y transfiere el resultado del ejercicio a la cuenta patrimonial <strong>3020 (Resultados de Ejercicios Anteriores)</strong>.
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', background: '#eff6ff', padding: '15px', borderRadius: '8px', border: '1px solid #bfdbfe', marginBottom: '25px' }}>
                        <IoCalendarOutline size={30} color="#3b82f6" />
                        <div style={{ flex: 1 }}>
                            <label style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#1e3a8a', display: 'block', marginBottom: '4px' }}>Selecciona Año Fiscal a Cerrar:</label>
                            <select 
                                value={selectedYear} 
                                onChange={e => setSelectedYear(Number(e.target.value))}
                                style={{ width: '120px', padding: '8px', borderRadius: '6px', border: '1px solid #93c5fd', fontSize: '1rem', fontWeight: 'bold', outline: 'none' }}
                                disabled={processing}
                            >
                                {[2024, 2025, 2026, 2027].map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </div>
                        <button 
                            className="primary-btn" 
                            style={{ margin: 0, padding: '10px 20px', background: '#1e3a8a', borderColor: '#1e3a8a' }}
                            onClick={handleRunCierre}
                            disabled={processing}
                        >
                            {processing ? 'Procesando...' : 'Generar Cierre'}
                        </button>
                    </div>

                    <h4 style={{ marginBottom: '10px', fontSize: '1.1rem', color: '#1f2937' }}>Historial de Cierres de Ejercicios</h4>
                    {loading ? <p>Cargando historial...</p> : existingCierres.length === 0 ? (
                        <p style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.9rem' }}>No se han registrado cierres de ejercicios contables en este condominio.</p>
                    ) : (
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflowX: 'auto' }}>
                            <table style={{ width: '100%', minWidth: '500px', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                <thead>
                                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', textAlign: 'left', color: '#6b7280' }}>
                                        <th style={{ padding: '10px' }}>Año</th>
                                        <th style={{ padding: '10px' }}>Fecha Cierre</th>
                                        <th style={{ padding: '10px', textAlign: 'right' }}>Monto Cuadrado</th>
                                        <th style={{ padding: '10px', textAlign: 'center' }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {existingCierres.map(c => (
                                        <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                            <td style={{ padding: '10px', fontWeight: 'bold', color: '#111827' }}>{c.year}</td>
                                            <td style={{ padding: '10px', color: '#4b5563' }}>{c.fecha}</td>
                                            <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>
                                                ${c.monto.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                            <td style={{ padding: '10px', textAlign: 'center' }}>
                                                <button 
                                                    onClick={() => setDeleteTarget(c)} 
                                                    className="icon-btn danger" 
                                                    title="Reabrir ejercicio fiscal (Borrar cierre)"
                                                    style={{ padding: '4px' }}
                                                    disabled={processing}
                                                >
                                                    <IoTrash size={16} /> Reabrir
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CierreFiscalModal;
